/**
 * iter84.15 — Magic-link claim page (/join/:token).
 * iter87.133 — First-time consent gate moved into `FirstTimeConsentModal`.
 *   Crewmate picks a username, clicks Continue → modal pops with 18+ +
 *   T&C checkboxes. Accepting sends `accepted_tc: true, age_confirmed: true`
 *   on the claim POST. Declining shows a friendly refusal card.
 *
 * Pre-fills league name, inviting admin, and email-on-file from the
 * server-resolved token.
 *
 * On success → drops them onto /league/:id with a welcome toast.
 *
 * Edge-case rendering (covered by the GET /api/auth/magic-link/:token
 * status field):
 *   - status "unknown" → "This invite link is not recognized."
 *   - status "expired" → "This link expired — ask your admin to resend."
 *   - status "used"    → "Already claimed; sign in with your codes."
 */
import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Crown, Lock, AlertTriangle, ExternalLink, KeyRound, XCircle } from "lucide-react";
import { API } from "@/lib/api";
import FirstTimeConsentModal from "@/components/FirstTimeConsentModal";
import { hasPlatformConsent, recordPlatformConsent } from "@/lib/platformConsent";

export default function JoinPage() {
    const { token } = useParams();
    const navigate = useNavigate();

    const [payload, setPayload] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [username, setUsername] = useState("");
    const [consentOpen, setConsentOpen] = useState(false);
    // iter87.133 — "declined" is a terminal state on this page: the user
    // clicked "Decline" in the consent modal. We surface a full-screen
    // refusal card and never send the claim POST.
    const [declined, setDeclined] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        fetch(`${API}/auth/magic-link/${encodeURIComponent(token)}`)
            .then((r) => r.json())
            .then((d) => {
                if (!cancelled) {
                    setPayload(d);
                    setUsername(d?.placeholder_username || "");
                }
            })
            .catch((e) => !cancelled && setError(e.message || "Failed to load invite"))
            .finally(() => !cancelled && setLoading(false));
        return () => { cancelled = true; };
    }, [token]);

    const status = payload?.status || "loading";

    const stateCopy = useMemo(() => {
        if (status === "expired") return {
            title: "This invite expired",
            body: "Magic-link invites are valid for 7 days. Ask your rivalry admin to resend the invite — your spot is still reserved.",
            icon: Lock,
        };
        if (status === "used") return {
            title: "Already claimed",
            body: "Looks like someone already used this magic link. If that was you, sign in with your invite code + personal code from the original email.",
            icon: KeyRound,
        };
        if (status === "unknown") return {
            title: "Invite not recognized",
            body: "This link doesn't match any invite. Double-check the URL from your email, or ask your rivalry admin to resend.",
            icon: AlertTriangle,
        };
        return null;
    }, [status]);

    // Step 1: user clicks "Continue" → validate username → open consent modal.
    const openConsent = (e) => {
        e?.preventDefault?.();
        setError(null);
        if (!username.trim()) { setError("Pick a username."); return; }
        // iter87.199.99 — Auto-skip the consent modal if this browser
        // has already recorded platform acceptance. Straight to claim
        // with the flags on; audit stamp preserved.
        if (hasPlatformConsent()) {
            handleAccept();
            return;
        }
        setConsentOpen(true);
    };

    // Step 2: user accepts both consents in the modal → submit claim.
    const handleAccept = async () => {
        setSubmitting(true);
        recordPlatformConsent();
        try {
            const r = await fetch(`${API}/auth/magic-link/${encodeURIComponent(token)}/claim`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    username: username.trim(),
                    accepted_tc: true,
                    age_confirmed: true,
                }),
            });
            const body = await r.json();
            if (!r.ok) {
                setError(body?.detail || "Could not join — try again.");
                setConsentOpen(false);
                return;
            }
            try {
                localStorage.setItem("pickem_session_v1", JSON.stringify({
                    participant_id: body.participant.id,
                    league_id: body.league.id,
                    username: body.participant.username,
                    is_admin: !!body.participant.is_admin,
                }));
            } catch (_) { /* localStorage may be disabled in private browsing */ }
            toast.success(`Welcome to ${body.league.name}, ${body.participant.username}!`);
            navigate(`/league/${body.league.id}`);
        } catch (err) {
            setError(err.message || "Network error — try again.");
            setConsentOpen(false);
        } finally {
            setSubmitting(false);
        }
    };

    // Step 2b: user declines → close modal and show refusal card.
    const handleDecline = () => {
        setConsentOpen(false);
        setDeclined(true);
    };

    if (loading) {
        return (
            <main className="min-h-screen flex items-center justify-center bg-ink text-zinc-400 text-sm">
                Loading invite…
            </main>
        );
    }

    // iter87.133 — Consent declined refusal card. Terminal state on this
    // page: no claim POST was sent, no participant record was created.
    if (declined) {
        return (
            <main className="min-h-screen flex items-center justify-center bg-ink p-4">
                <div
                    data-testid="join-consent-declined"
                    className="brutal-card max-w-md w-full p-6 space-y-4 text-center"
                >
                    <XCircle className="w-10 h-10 mx-auto text-heat" />
                    <h1 className="font-display font-black text-xl uppercase tracking-tight">
                        Can't enter the Rivalry
                    </h1>
                    <p className="text-sm text-zinc-400 leading-relaxed">
                        To join, you must be <strong className="text-white">18 or older</strong> and accept the{" "}
                        <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-cyber underline">Terms & Conditions</a>.
                        You can close this tab, or contact your rivalry admin if you have questions.
                    </p>
                    <button
                        type="button"
                        data-testid="join-declined-retry-btn"
                        onClick={() => { setDeclined(false); }}
                        className="brutal-btn inline-flex items-center gap-2 text-xs"
                    >
                        Change my mind
                    </button>
                </div>
            </main>
        );
    }

    // Error / used / expired states share a card layout.
    if (stateCopy) {
        const Icon = stateCopy.icon;
        return (
            <main className="min-h-screen flex items-center justify-center bg-ink p-4">
                <div data-testid={`join-state-${status}`} className="brutal-card max-w-md w-full p-6 space-y-4 text-center">
                    <Icon className="w-10 h-10 mx-auto text-heat" />
                    <h1 className="font-display font-black text-xl uppercase tracking-tight">{stateCopy.title}</h1>
                    <p className="text-sm text-zinc-400 leading-relaxed">{stateCopy.body}</p>
                    <a
                        href="/"
                        data-testid="join-back-home-btn"
                        className="brutal-btn inline-flex items-center gap-2 text-xs"
                    >
                        Go to sign-in <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                </div>
            </main>
        );
    }

    // OK — render the join form.
    return (
        <main className="min-h-screen bg-ink p-4 sm:p-8 flex items-center justify-center">
            <form
                onSubmit={openConsent}
                data-testid="join-form"
                className="brutal-card max-w-md w-full p-6 space-y-5"
            >
                <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-cyber border-2 border-black shadow-brutalYellow flex items-center justify-center shrink-0">
                        <Crown className="w-5 h-5 text-black" strokeWidth={2.5} />
                    </div>
                    <div>
                        <p className="text-[10px] uppercase tracking-widest text-zinc-500">Tiny Crew Rivalries</p>
                        <h1 className="font-display font-black text-xl text-white leading-tight">
                            Join <span className="text-cyber">{payload.league_name}</span>
                        </h1>
                        <p className="text-xs text-zinc-400 mt-1">
                            Invited by <strong className="text-white">{payload.admin_username}</strong>
                        </p>
                    </div>
                </div>

                {payload.email && (
                    <div className="text-xs">
                        <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Email on file</p>
                        <div data-testid="join-email-pill" className="pill bg-ink border-[#333] text-zinc-300 text-[11px]">
                            {payload.email}
                        </div>
                    </div>
                )}

                <label className="block">
                    <span className="text-[10px] uppercase tracking-widest text-zinc-500">Pick a username</span>
                    <input
                        data-testid="join-username-input"
                        type="text"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        placeholder="e.g. SkyHawk22"
                        maxLength={40}
                        autoFocus
                        className="brutal-input w-full mt-1"
                    />
                    <span className="text-[10px] text-zinc-600 mt-1 block">
                        Case doesn't matter for signing in — TKA and tka are treated the same.
                    </span>
                </label>

                {error && (
                    <p data-testid="join-error" className="text-xs text-heat">{error}</p>
                )}

                <button
                    data-testid="join-submit-btn"
                    type="submit"
                    disabled={submitting}
                    className="brutal-btn bg-cyber text-black w-full font-display font-black text-sm"
                >
                    Continue
                </button>

                {(payload.invite_code || payload.personal_code) && (
                    <div className="border-t border-[#222] pt-4 mt-2">
                        <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">
                            Save these for future logins
                        </p>
                        <ul className="space-y-1.5 text-[11px]">
                            {payload.invite_code && (
                                <li className="flex items-baseline justify-between gap-2">
                                    <span className="text-zinc-500">Rivalry code</span>
                                    <code data-testid="join-invite-code" className="font-mono text-cyber">{payload.invite_code}</code>
                                </li>
                            )}
                            {payload.personal_code && (
                                <li className="flex items-baseline justify-between gap-2">
                                    <span className="text-zinc-500">Personal code</span>
                                    <code data-testid="join-personal-code" className="font-mono text-volt">{payload.personal_code}</code>
                                </li>
                            )}
                        </ul>
                        <p className="text-[10px] text-zinc-600 mt-2 leading-snug">
                            This magic link expires in 7 days. The codes above are your <strong className="text-zinc-400">permanent</strong> sign-in credentials.
                        </p>
                    </div>
                )}
            </form>

            <FirstTimeConsentModal
                open={consentOpen}
                leagueName={payload?.league_name}
                onAccept={handleAccept}
                onDecline={handleDecline}
                submitting={submitting}
            />
        </main>
    );
}
